/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect } from 'react';
import {
    object, string, func, shape,
} from 'prop-types';
import { useDispatch } from 'react-redux';

import ComponentErrorBoundary from '../Common/ComponentErrorBoundary/ComponentErrorBoundary';
import SimpleCarouselBuilder from '../Common/SimpleCarouselBuilder';
import useClickStreamCustomEventsTracking from '../../../../helpers/Personalization/useClickstreamCustomEventsTracking';
import noop from '../../../../helpers/noop';
import { emitProductImpression, emitCustomTrackingEvent } from '../../../../../state/ducks/TagManager/ducks/ClickStreamEvents/ClickStreamEvent-Actions';
import RecentlyViewedGridUI from './RecentlyViewedGridUI/RecentlyViewedGridUI';
import { trackEvent } from '../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';

const RecentlyViewedRecsBuilder = ({
    data, blockObj, setShowSkeleton,
}) => {
    const dispatch = useDispatch();

    const {
        products_to_show,
        min_products_to_show,
        title,
        css_classes,
        title_margin,
        title_font_color,
        title_font_size,
        title_font_family,
        title_font_style,
        align_title = 'center',
        padding,
    } = blockObj || {};

    const trackClickThrough = useClickStreamCustomEventsTracking({
        salesforceResponse: data?.recentlyViewedRecs,
        isFireImpression: false, // don't fired impression from here
        page: { type: 'home' },
    });

    useEffect(() => {
        setShowSkeleton(false);

        const hasProducts = (data?.recentlyViewedRecs?.products?.length > 0)
            || (data?.recentlyViewedRecs?.productRecs?.length > 0);

        if (!hasProducts) return;

        const displayType = data?.recentlyViewedRecs?.campaign?.campaignResponses?.[0]?.payload?.displayType || '';
        const products = data?.recentlyViewedRecs?.products;

        dispatch(emitCustomTrackingEvent({
            action: 'Impression',
            salesforceResponse: data?.recentlyViewedRecs,
            page: { type: 'home' },
            eventSubType: 'campaign-tracking',
            displayType,
            min_products_to_show,
        }));

        if ((displayType === 'BOTH' || displayType === 'CUST_PRODUCTS_ONLY') && products?.length >= min_products_to_show) {
            dispatch(emitProductImpression({
                action: 'Impression',
                productsPayload: {
                    products,
                    mobileBreakpoint: 600,
                },
                salesforceResponse: data?.recentlyViewedRecs,
                page: { type: 'home' },
            }));
        }
    }, []);

    const handleClick = (partNumber, isFromProductRecs) => {
        const gaTagging = data?.recentlyViewedRecs?.productsContent?.ga_tagging || {};
        const gaTagging1 = data?.recentlyViewedRecs?.productRecsContent?.ga_tagging || {};
        const trackingData = isFromProductRecs ? gaTagging1 : gaTagging;

        trackClickThrough(partNumber);
        dispatch(trackEvent({
            eventCategory: trackingData?.event_category || 'Product',
            eventAction: trackingData?.event_action || 'Click',
            eventLabel: `${trackingData?.event_label} - ${partNumber}` || 'Recently Viewed',
            eventName: 'feature_interaction',
            feature_category: trackingData?.event_category || 'Product',
            feature_element: trackingData?.event_action || 'Click',
            feature_detail: `${trackingData?.event_label} - ${partNumber}` || 'Recently Viewed',
        }));
    };

    const renderCarousel = (carouselTitle, products, isFromProductRecs) => (
        <ComponentErrorBoundary function="RecentlyViewedRecsSFContainer" module="PersonalizedContent" message="Something went wrong when displaying Recently Viewed Recommendations from Salesforce">
            <SimpleCarouselBuilder
                blockData={{
                    productsToShow: products_to_show,
                    title: carouselTitle,
                    css_classes,
                    titleMargin: title_margin,
                    titleFontColor: title_font_color?.color,
                    titleFontSize: title_font_size,
                    titleFontStyle: title_font_style,
                    titleFontFamily: title_font_family,
                    alignTitle: align_title,
                    padding,
                }}
                productClickCallback={(partNumber) => handleClick(partNumber, isFromProductRecs)}
                products={products}
                demandType="XS"
            />
        </ComponentErrorBoundary>
    );

    if ((data?.recentlyViewedRecs?.products && data?.recentlyViewedRecs?.products.length > 0)
        || (data?.recentlyViewedRecs?.productRecs && data?.recentlyViewedRecs?.productRecs.length > 0)) {
        const titleSF = data?.recentlyViewedRecs?.campaign?.campaignResponses?.[0]?.payload?.productRecTitle || data?.recentlyViewedRecs?.campaign?.campaignResponses?.[0]?.payload?.fallbackProductRecTitle;
        const displayType = data?.recentlyViewedRecs?.campaign?.campaignResponses?.[0]?.payload?.displayType || '';
        const products = data?.recentlyViewedRecs?.products;
        const productRecs = data?.recentlyViewedRecs?.productRecs;
        const isGridView = data?.recentlyViewedRecs?.campaign?.campaignResponses?.[0]?.experienceName?.includes('GRID');
        const isNewDesign = data?.recentlyViewedRecs?.campaign?.campaignResponses?.[0]?.experienceName === 'New Design';

        switch (displayType) {
            case 'BOTH':
                if (isGridView || isNewDesign) {
                    return (
                        <ComponentErrorBoundary function="RecentlyViewedGridUI" module="PersonalizedContent" message="Something went wrong when displaying Recently Viewed Recommendations from Salesforce">
                            <RecentlyViewedGridUI sfData={data} cmsData={blockObj} isNewDesign={isNewDesign} handleClickCallback={handleClick} />
                        </ComponentErrorBoundary>
                    );
                }
                return (
                    <>
                        {renderCarousel(title || 'Recently Viewed Products', products?.length >= min_products_to_show ? products : [], false)}
                        {renderCarousel(titleSF, productRecs, true)}
                    </>
                );

            case 'CUST_PRODUCTS_ONLY':
                if (isGridView || isNewDesign) {
                    return (
                        <ComponentErrorBoundary function="RecentlyViewedGridUI" module="PersonalizedContent" message="Something went wrong when displaying Recently Viewed Recommendations from Salesforce">
                            <RecentlyViewedGridUI sfData={data} cmsData={blockObj} isNewDesign={isNewDesign} handleClickCallback={handleClick} />
                        </ComponentErrorBoundary>
                    );
                }
                return renderCarousel(title || 'Recently Viewed Products', products?.length >= min_products_to_show ? products : [], false);

            case 'PRODUCT_RECS_ONLY':
                if (isGridView || isNewDesign) {
                    return (
                        <ComponentErrorBoundary function="RecentlyViewedGridUI" module="PersonalizedContent" message="Something went wrong when displaying Recently Viewed Recommendations from Salesforce">
                            <RecentlyViewedGridUI sfData={data} cmsData={blockObj} isNewDesign={isNewDesign} handleClickCallback={handleClick} />
                        </ComponentErrorBoundary>
                    );
                }
                return renderCarousel(titleSF, productRecs, true);

            default:
                return null;
        }
    }
    return null;
};

RecentlyViewedRecsBuilder.propTypes = {
    data: object.isRequired,
    blockObj: shape({
        title_font_color: shape({
            color: string.isRequired,
        }),
        title_font_size: string,
        title_font_style: string,
        title_font_family: string,
        padding: string,
    }).isRequired,
    setShowSkeleton: func,
};

RecentlyViewedRecsBuilder.defaultProps = {
    setShowSkeleton: noop,
};

export default RecentlyViewedRecsBuilder;
